import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import type { LocationQueryRaw } from 'vue-router';

const STORAGE_KEY = 'booking-last-search-query';

/**
 * Remembers the subscriber's booking search so it can be replayed once the flow has moved past
 * the results, where the URL no longer carries it.
 *
 * Owns the value, its capture, its persistence and its replay, so no store or unrelated
 * composable has to.
 */
export function useLastSearchQuery() {
  const { searchQueryParams } = useSearchParamsAdapter();
  const bookingSelectionStore = useBookingSelectionStore();
  const localePath = useLocalePath();
  const { goHome } = useHome();
  // A null default leaves the serializer unguessable, and the query would round-trip as
  // "[object Object]", so the object serializer is set explicitly.
  const lastSearchQuery = useLocalStorage<LocationQueryRaw | null>(STORAGE_KEY, null, {
    serializer: StorageSerializers.object,
  });

  // A route that carries no search, the payment page for instance, must not erase the saved one.
  watch(searchQueryParams, (search) => {
    if (search.origin && search.destination) {
      lastSearchQuery.value = { ...search };
    }
  }, { immediate: true });

  /**
   * Returns the subscriber to the results of the saved search, dropping the dead selection.
   *
   * Falls back to home when no search is saved, because a param-less /book/select is bounced by
   * the route guard with a second, unrelated warning.
   */
  function goBackToSearch() {
    const query = lastSearchQuery.value;
    bookingSelectionStore.clearSelection();

    if (!query) return goHome();

    return navigateTo(localePath({ path: '/book/select', query }));
  }

  return { lastSearchQuery, goBackToSearch };
}
